import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import { CommonButton } from '@/components/common'
import {
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import {
  AppointmentCard,
  CancelConfirmationDialog,
} from '@/components/donation'
import { useDonorContext, useTenantContext } from '@/context'
import { useGetDonorAppointments, useCancelDonorAppointment } from '@/hooks'
import { getDonorFullName, formatUtcDate, formatDateString } from '@/utils'
import { STORAGE_KEYS } from '@/constants'
import { localStorageService, sessionStorageService } from '@/services'
import {
  StyledPageTitle,
  StyledSectionTitle,
  StyledDonorSummary,
  StyledSummaryGreeting,
  StyledSummaryGrid,
  StyledSummaryCard,
  StyledIconWrapper,
  StyledSummaryText,
  StyledAppointmentsList,
} from './styled'
import { AppointmentsPageSkeleton, AppointmentCardSkeleton } from './Skeletons'

export const DonorAppointments = () => {
  const { donor } = useDonorContext()
  const { tenant } = useTenantContext()
  const navigate = useNavigate()
  const { getDonorAppointments, loadingGetDonorAppointments } =
    useGetDonorAppointments()
  const { cancelDonorAppointment, loadingCancelDonorAppointment } =
    useCancelDonorAppointment()

  const [showAll, setShowAll] = useState(false)
  const [fetchingMore, setFetchingMore] = useState(false)
  const [donorAppointmentsData, setDonorAppointmentsData] = useState(null)
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [appointmentToCancel, setAppointmentToCancel] = useState(null)

  const fetchDonorAppointments = async (
    showAllFlag = false,
    loadingFlag = true
  ) => {
    if (!donor?.id) return
    const response = await getDonorAppointments(
      donor?.id,
      showAllFlag,
      loadingFlag
    )
    setDonorAppointmentsData(response?.data || null)
  }

  useEffect(() => {
    // Clear any stale reschedule state when the user returns to this page
    sessionStorageService.removeItem(STORAGE_KEYS.RESCHEDULE_APPOINTMENT_ID)
    fetchDonorAppointments()
  }, [donor?.id])

  const upcomingAppointments = useMemo(() => {
    if (!donorAppointmentsData?.upcoming_appointments) return []
    return donorAppointmentsData?.upcoming_appointments
  }, [donorAppointmentsData])

  const pastDonations = useMemo(() => {
    return donorAppointmentsData?.past_donations || []
  }, [donorAppointmentsData])

  const totalDonations = useMemo(() => {
    return donorAppointmentsData?.total_donations || 0
  }, [donorAppointmentsData])

  const totalGallons = useMemo(() => {
    return donorAppointmentsData?.gallon_award1 ?? 0
  }, [donorAppointmentsData])

  const totalGallons2 = useMemo(() => {
    return donorAppointmentsData?.gallon_award2 ?? 0
  }, [donorAppointmentsData])

  const showLifetimeDonations = useMemo(() => {
    const config = tenant?.configuration_options?.find(
      item => item.code === 'SHOW_LIFETIME_DONATIONS'
    )
    return config?.value === 'true'
  }, [tenant])

  const useGalloneer = useMemo(() => {
    const config = tenant?.configuration_options?.find(
      item => item.code === 'USE_GALLONEER'
    )
    return config?.value === 'true'
  }, [tenant])

  const useGalloneer2 = useMemo(() => {
    const config = tenant?.configuration_options?.find(
      item => item.code === 'USE_GALLONEER2'
    )
    return config?.value === 'true'
  }, [tenant])

  const galloneerLabel = useMemo(() => {
    const config = tenant?.configuration_options?.find(
      item => item.code === 'GALLONEER_LABEL'
    )
    return config?.value ?? ''
  }, [tenant])

  const galloneerLabel2 = useMemo(() => {
    const config = tenant?.configuration_options?.find(
      item => item.code === 'GALLONEER_LABEL2'
    )
    return config?.value ?? ''
  }, [tenant])

  const isInEligible = useMemo(() => {
    return donorAppointmentsData?.eligibility_next_date === '9999-09-09'
  }, [donorAppointmentsData])

  const nextEligibilityDate = useMemo(() => {
    const eligibilityDate = donorAppointmentsData?.eligibility_next_date
    const procedure = donorAppointmentsData?.procedure

    const donateToday = `You can donate ${procedure ? ` ${procedure}` : ''} again today`
    const ineligibleToday = `You are ineligible to donate ${procedure ? ` ${procedure}` : ''} today`

    if (!eligibilityDate) return donateToday
    if (isInEligible) return ineligibleToday

    const date = new Date(eligibilityDate)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    date.setHours(0, 0, 0, 0)

    if (date <= today) return donateToday

    return `You can donate ${procedure ? ` ${procedure}` : ''} again on ${formatDateString(eligibilityDate)}`
  }, [donorAppointmentsData])

  const handleCancelClick = appointment => {
    setAppointmentToCancel(appointment)
    setCancelModalOpen(true)
  }

  const handleRescheduleClick = appointment => {
    sessionStorageService.setItem(
      STORAGE_KEYS.RESCHEDULE_APPOINTMENT_ID,
      {
        id: appointment.id,
        date: appointment.date,
        start_time: appointment.start_time,
        location: appointment.donor_center_name,
        procedure_type: appointment.donation_type,
        address: appointment.address,
      },
      true
    )
    navigate('/')
  }

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel?.id) return

    await cancelDonorAppointment(appointmentToCancel.id)
    await fetchDonorAppointments()
    setCancelModalOpen(false)
    setAppointmentToCancel(null)
  }

  const handleCancelModalClose = () => {
    setCancelModalOpen(false)
    setAppointmentToCancel(null)
  }

  const handleToggleShowAll = async () => {
    const showAllFlag = !showAll
    setShowAll(showAllFlag)
    setFetchingMore(true)
    try {
      await fetchDonorAppointments(showAllFlag, false)
    } finally {
      setFetchingMore(false)
    }
  }

  const summaryCards = useMemo(() => {
    const cards = []
    cards.push({ id: 'donor-info', type: 'donor-info' })
    if (showLifetimeDonations) {
      cards.push({ id: 'lifetime', type: 'lifetime' })
    }
    if (useGalloneer) {
      cards.push({ id: 'gallons', type: 'gallons' })
    }
    if (useGalloneer2) {
      cards.push({ id: 'gallons2', type: 'gallons2' })
    }
    cards.push({ id: 'eligibility', type: 'eligibility' })
    return cards
  }, [showLifetimeDonations, useGalloneer, useGalloneer2])

  const shouldShowToggleButton = useMemo(() => {
    if (fetchingMore || showAll) return false
    return !!(donorAppointmentsData?.total_donations > 10)
  }, [donorAppointmentsData?.total_donations, showAll, fetchingMore])

  if (loadingGetDonorAppointments) {
    return <AppointmentsPageSkeleton />
  }

  return (
    <>
      <StyledPageTitle>Welcome {getDonorFullName(donor)}</StyledPageTitle>
      <StyledDonorSummary>
        <StyledSummaryGrid
          sx={{
            gridTemplateColumns: {
              xs: '1fr',
              sm: `repeat(${summaryCards.length}, minmax(0, 1fr))`,
            },
          }}
        >
          {summaryCards.map(card => {
            if (card.type === 'donor-info') {
              return (
                <StyledSummaryCard key={card.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      <StyledSummaryText
                        sx={{
                          width: '50%',
                          textAlign: 'right',
                          flexShrink: 0,
                        }}
                      >
                        Donor Number:
                      </StyledSummaryText>
                      <StyledSummaryText
                        sx={{ color: 'primary.main', fontWeight: 600 }}
                      >
                        {donor?.donor_number || '-'}
                      </StyledSummaryText>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      <StyledSummaryText
                        sx={{
                          width: '50%',
                          textAlign: 'right',
                          flexShrink: 0,
                        }}
                      >
                        Blood Type:
                      </StyledSummaryText>
                      <StyledSummaryText
                        sx={{ color: 'primary.main', fontWeight: 600 }}
                      >
                        {donor?.blood_group_id?.name || '-'}
                      </StyledSummaryText>
                    </Box>
                  </Box>
                </StyledSummaryCard>
              )
            }
            if (card.type === 'lifetime') {
              return (
                <StyledSummaryCard key={card.id}>
                  <StyledIconWrapper>
                    <CalendarIcon sx={{ fontSize: 32 }} />
                  </StyledIconWrapper>
                  <StyledSummaryText>
                    {totalDonations} Lifetime Donations
                  </StyledSummaryText>
                </StyledSummaryCard>
              )
            }
            if (card.type === 'gallons') {
              const labelPart = galloneerLabel ? `${galloneerLabel} ` : ''
              return (
                <StyledSummaryCard key={card.id}>
                  <StyledIconWrapper>
                    <PeopleIcon sx={{ fontSize: 32 }} />
                  </StyledIconWrapper>
                  <StyledSummaryText>
                    {totalGallons > 0
                      ? `${totalGallons} ${labelPart}gallons donated`
                      : `On your way to 1 ${labelPart}gallon`}
                  </StyledSummaryText>
                </StyledSummaryCard>
              )
            }
            if (card.type === 'gallons2') {
              const labelPart = galloneerLabel2 ? `${galloneerLabel2} ` : ''
              return (
                <StyledSummaryCard key={card.id}>
                  <StyledIconWrapper>
                    <PeopleIcon sx={{ fontSize: 32 }} />
                  </StyledIconWrapper>
                  <StyledSummaryText>
                    {totalGallons2 > 0
                      ? `${totalGallons2} ${labelPart}gallons donated`
                      : `On your way to 1 ${labelPart}gallon`}
                  </StyledSummaryText>
                </StyledSummaryCard>
              )
            }
            if (card.type === 'eligibility') {
              return (
                <StyledSummaryCard key={card.id}>
                  <StyledIconWrapper>
                    {isInEligible ? (
                      <CancelIcon sx={{ fontSize: 32 }} />
                    ) : (
                      <CheckCircleIcon sx={{ fontSize: 32 }} />
                    )}
                  </StyledIconWrapper>
                  <StyledSummaryText>
                    {nextEligibilityDate || 'No upcoming eligibility date'}
                  </StyledSummaryText>
                </StyledSummaryCard>
              )
            }
            return null
          })}
        </StyledSummaryGrid>
      </StyledDonorSummary>
      <StyledSectionTitle>Upcoming Appointments</StyledSectionTitle>
      <StyledAppointmentsList>
        {upcomingAppointments.length > 0 ? (
          upcomingAppointments.map(appointment => (
            <AppointmentCard
              key={appointment.id}
              date={appointment.date}
              time={appointment.start_time}
              location={appointment.donor_center_name}
              procedureType={appointment.donation_type}
              address={appointment.address}
              room={appointment.room}
              isUpcoming={true}
              onCancel={() => handleCancelClick(appointment)}
              onReschedule={() => handleRescheduleClick(appointment)}
            />
          ))
        ) : (
          <Box sx={{ py: 2 }}>
            <StyledSummaryText>No upcoming appointments</StyledSummaryText>
          </Box>
        )}
      </StyledAppointmentsList>
      <StyledSectionTitle>Your Past Appearances</StyledSectionTitle>
      <StyledAppointmentsList>
        {pastDonations.length > 0 ? (
          <>
            {pastDonations.map(donation => (
              <AppointmentCard
                key={donation.id}
                date={donation.donation_date}
                location={donation.donor_center_name}
                procedureType={donation.donation_type}
                address={donation.address}
              />
            ))}
            {fetchingMore && (
              <>
                {[1, 2, 3].map(index => (
                  <Box
                    key={`loading-skeleton-${index}`}
                    sx={{
                      animation: `fadeInExpand 0.4s ease-out ${index * 0.1}s both`,
                      '@keyframes fadeInExpand': {
                        from: {
                          opacity: 0,
                          transform: 'translateY(-20px) scale(0.95)',
                          maxHeight: 0,
                          marginBottom: 0,
                        },
                        to: {
                          opacity: 1,
                          transform: 'translateY(0) scale(1)',
                          maxHeight: '200px',
                          marginBottom: '12px',
                        },
                      },
                    }}
                  >
                    <AppointmentCardSkeleton isUpcoming={false} />
                  </Box>
                ))}
              </>
            )}
            {shouldShowToggleButton && !fetchingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CommonButton
                  id='toggle-show-all-btn'
                  label={showAll ? 'Show Less' : 'Show All'}
                  variant='primary'
                  color='primary'
                  onClick={handleToggleShowAll}
                />
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ py: 2 }}>
            <StyledSummaryText>No past appearances</StyledSummaryText>
          </Box>
        )}
      </StyledAppointmentsList>
      <CancelConfirmationDialog
        open={cancelModalOpen}
        onClose={handleCancelModalClose}
        onConfirm={handleConfirmCancel}
        loading={loadingCancelDonorAppointment}
      />
    </>
  )
}
